import { useEffect, useState } from 'react';
import './web/resources/themeContent/styles/fontawesome.min.css'
import './web/resources/themeContent/styles/animate.min.css'
import './web/resources/themeContent/styles/slick.css'
import './web/resources/themeContent/styles/slick-theme.css'
import 'react-toastify/dist/ReactToastify.css';
import 'react-accessible-accordion/dist/fancy-example.css';
import './web/resources/themeContent/styles/global.scss';
import './web/resources/themeContent/styles/style-arabic.css';
import { routerConfig } from './RouteConfig';
import { getLanguageCodeFromSession } from './helpers/CommonHelper';
import Config from './helpers/Config';
import { useGetSiteInfoQuery, useGetSiteLogoQuery } from './services/site';
import { useAppDispatch } from './stateManagment/reduxStore';
import rootAction from './stateManagment/actions/rootAction';
import { RouterProvider } from 'react-router-dom';
import GoTop from './web/components/layout/GoTop';
import { useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import LoadingScreen from './web/components/shared/LoadingScreen.js';
import { ToggleProvider } from './ToggleProvider.js';
import { PerspectiveSwitchModal } from './web/components/modal/PerspectiveSwitchModal.js';
import { useGetContentAssetCacheQuery } from './services/content';
import MaintenanceLayout from './web/components/layout/MaintenanceLayout';
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { getResolvedLogRocketAppId, initLogRocket } from './helpers/LogRocket';
import { logApiError } from './helpers/TelemetryHelper';

const AppHelmet = () => {
  const dispatch = useAppDispatch();
  const [langCode, setLangCode] = useState('en');
  const { retokenizing } = useSelector(state => state.commonReducer);
  useGetSiteLogoQuery();

  useEffect(() => {
    dispatch(rootAction.commonAction.setLoading(false));
    dispatch(rootAction.checkoutActions.setIsInOrderReview(false));
    if (retokenizing) {
      dispatch(rootAction.commonAction.isRetokenizing(false));
    }
  }, [dispatch]);

  useEffect(() => {
    try {
      let lnCode = getLanguageCodeFromSession();
      setLangCode(lnCode);
      if (langCode === Config.LANG_CODES_ENUM["Arabic"]) {
        document.documentElement.lang = Config.LANG_CODES_ENUM["Arabic"];
      } else {
        document.documentElement.lang = Config.LANG_CODES_ENUM["English"];
      }
    } catch (error) {
      console.error('An error occurred:', error.message);
      document.documentElement.lang = Config.LANG_CODES_ENUM["English"];
    }
  }, [langCode]);

  return null;
}

function App() {
  const dispatch = useAppDispatch();
  const [maintenanceCheckComplete, setMaintenanceCheckComplete] = useState(false);
  const [previousMaintenanceStatus, setPreviousMaintenanceStatus] = useState(null);
  const { isLoading: isSiteInfoLoading, data: siteInfoData, isError: isSiteInfoError, error: siteInfoError } = useGetSiteInfoQuery();
  const { isLoading: isAccountLoginIntroTextLoading } = useGetContentAssetCacheQuery('account-login-intro-text');
  const { isLoading: isSSOLoginButtonTextLoading } = useGetContentAssetCacheQuery('sso-login-button-text');
  const { storefrontStatus } = useSelector(state => state.commonReducer);
  const user = useSelector(state => state.userReducer.user);

  // Stupid simple maintenance flag loader from a public JSON file.
  // Expected formats (any of these will work):
  // 1) { "sites": { "localhost:3000": { "status": "maintenance", "siteId": "UA_TEST_S" } } }
  // 2) { "localhost:3000": { "status": "maintenance", "siteId": "UA_TEST_S" } }
  // 3) [ { "host": "localhost:3000", "status": "maintenance", "siteId": "UA_TEST_S" } ]

  const checkMaintenanceStatus = async () => {
    const fileName = window.location.host.indexOf('stg-cf-') === -1 && window.location.host.indexOf('localhost') === -1 ? 'maintenance.json' : 'maintenance-stg.json';
    const host = (window.location.host || '').toLowerCase().replace('stg-cf-', '');
    try {
      const res = await fetch(`https://images.uniformadvantage.com/Maintenance/${fileName}`, { cache: 'no-store' });
      if (!res.ok) return;
      const json = await res.json();
      let entry = null;

      if (Array.isArray(json)) {
        entry = json.find(x => (x.host || x.hostname || '').toLowerCase() === host) || null;
      } else if (json && typeof json === 'object') {
        if (json.sites && typeof json.sites === 'object') {
          entry = json.sites[host] || null;
        }
        if (!entry && json[host]) {
          entry = json[host];
        }
      }

      if (!entry) return;
      const status = (entry.status || entry.storefrontStatus || '').toLowerCase();
      const siteId = entry.siteId || entry.id;
      if (siteId) {
        dispatch(rootAction.commonAction.setSiteId(siteId));
      }
      if (status === 'maintenance') {
        dispatch(rootAction.commonAction.setStorefrontStatus('maintenance'));
      } else {
        dispatch(rootAction.commonAction.setStorefrontStatus('online'));
      }
    } catch (_) {
      // ignore
    }
  };

  useEffect(() => {
    // Initial check
    checkMaintenanceStatus().finally(() => setMaintenanceCheckComplete(true));

    // Periodic check every 30 seconds
    const interval = setInterval(checkMaintenanceStatus, 30000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Track maintenance status changes and reload when coming back online
  useEffect(() => {
    if (previousMaintenanceStatus === null) {
      // First time setting the status, just track it
      setPreviousMaintenanceStatus(storefrontStatus);
    } else if (previousMaintenanceStatus === 'maintenance' && storefrontStatus === 'online') {
      // Site came back online from maintenance - reload the page
      window.location.reload();
    } else {
      // Update the previous status for next comparison
      setPreviousMaintenanceStatus(storefrontStatus);
    }
  }, [storefrontStatus, previousMaintenanceStatus]);

  useEffect(() => {
    if (!isSiteInfoError) return;
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    logApiError('SITE_INFO_ERROR_OFFLINE_SCREEN', 'site-config', {
      isSiteInfoLoading,
      storefrontStatus,
      maintenanceCheckComplete,
      hasUserID: !!user?.UserID,
      referrer: document.referrer,
      isOnline: navigator.onLine,
      networkInfo: conn ? {
        effectiveType: conn.effectiveType,
        downlink: conn.downlink,
        rtt: conn.rtt,
        saveData: conn.saveData,
      } : null,
      siteInfoError: {
        status: siteInfoError?.status,
        data: siteInfoError?.data,
        error: siteInfoError?.error,
      },
    });
  }, [isSiteInfoError]);

  // Initialise before child effects (e.g. auth identify) run — initLogRocket is idempotent.
  if (siteInfoData) {
    initLogRocket(getResolvedLogRocketAppId(siteInfoData));
  }

  // Session restoration: Re-identify user on page load/refresh if they have a valid session
  useEffect(() => {
    const restoreSession = async () => {
      // Only proceed if LogRocket is initialized, site-config loaded, and user has valid session
      if (!siteInfoData || !user?.JWTToken || !user?.BECustNo) return;

      const ipHash = siteInfoData.lrClientIpHash;
      const { identifyUser } = await import('./helpers/LogRocket');
      await identifyUser(user, null, ipHash).catch(() => {});
    };

    restoreSession();
  }, [siteInfoData, user?.JWTToken, user?.BECustNo]);

  // Block app loading until maintenance check completes
  if (!maintenanceCheckComplete || ((isSiteInfoLoading || isAccountLoginIntroTextLoading || isSSOLoginButtonTextLoading) && !siteInfoData)) {
    return <div className='text-center'>
      <LoadingScreen loading={true} detailText='Loading...' />
    </div>
  }

  if (storefrontStatus === 'maintenance') {
    return <MaintenanceLayout />
  }

  if (isSiteInfoError) {
    // return a centered logo horizontally and vertically with a message that site is not available
    return <div className='text-center d-flex flex-column justify-content-center align-items-center' style={{ height: '100vh' }}>
      <img alt="Uniform Advantage" src="https://www.uniformadvantage.com/on/demandware.static/-/Sites-UA-Library/default/dw33f38775/images/footer/uniform-advantage.svg" />
      <h3 className="mt-4">The site is offline. Please try again after sometime. If the problem persists please call our helpdesk/support team at 800.283.8708</h3>
    </div>
  }

  const PAYPAL_OPTIONS = {
    clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID,
    // "currency": "USD",
    intent: "authorize",
    components: "buttons,messages,applepay",
    enableFunding: "paylater",
    commit: false,
    disableFunding: "venmo"
  };

  return (
    <PayPalScriptProvider options={PAYPAL_OPTIONS} deferLoading={true}>
      <ToggleProvider>
        <AppHelmet />
        <PerspectiveSwitchModal />

        <RouterProvider router={routerConfig} />
        <GoTop scrollStepInPx="3000" delayInMs="0.25" />
        <ToastContainer
          style={{ width: 'auto' }}
          position="bottom-right"
          autoClose={false}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          limit={1}
        />
      </ToggleProvider>
    </PayPalScriptProvider>
  );

}

export default App;
